import { useState, useEffect } from 'react';

import { Utils } from '@mvrb-frontend/shared-ui';

import { ResponseException } from '../models';
import { AxiosPrivate } from '../utils/axios';
import { PaginationQuery } from '../interfaces/paginationQuery.interface';
import { DistributionListModel, DistributionListItemModel } from '../models';

export const useDistributionList = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [items, setItems] = useState<DistributionListItemModel[]>([]);
  const [count, setCount] = useState<number>(0);
  const [errors, setErrors] = useState<string[]>([]);
  const [displayErrors, setDisplayErrors] = useState(false);

  useEffect(() => {
    setDisplayErrors(!isFetching && !success && errors.length > 0);
  }, [isFetching, success, errors]);

  const getAll = async (query: PaginationQuery) => {
    try {
      setIsFetching(true);

      const { data } = await AxiosPrivate.get('/distribution-list', { params: { ...query } });
      const distributionLists = data.distributionLists.map(dl => new DistributionListItemModel(dl));

      setCount(data.count);
      setItems(distributionLists);
    } catch (e) {
    } finally {
      setIsFetching(false);
    }
  };

  const getDetails = async (id: string) => {
    try {
      setIsFetching(true);
      return await AxiosPrivate.get(`/distribution-list/${id}`);
    } catch (e) {
      setErrors(new ResponseException(e).getErrorMessage());
    } finally {
      setIsFetching(false);
    }
  };

  const submit = async (distributionList: DistributionListModel) => {
    if (distributionList.individualUserIds?.length > 0) {
      distributionList.users = distributionList.users.concat(
        distributionList.individualUserIds.map(id => ({
          id,
        })),
      );
    }
    const payload = Utils.nullifyEmptyStringProperties(distributionList);
    try {
      setIsFetching(true);
      if (distributionList.id) {
        await AxiosPrivate.patch(`/distribution-list/${distributionList.id}`, payload);
      } else {
        await AxiosPrivate.post(`/distribution-list`, payload);
      }
      setSuccess(true);
    } catch (e) {
      setErrors(new ResponseException(e).getErrorMessage());
      setSuccess(false);
    } finally {
      setIsFetching(false);
    }
  };

  const duplicate = async (id: string) => {
    try {
      setIsFetching(true);
      const { data } = await AxiosPrivate.post(`/distribution-list/duplicate/${id}`);
      setSuccess(true);
      return data;
    } catch (e) {
      setErrors(new ResponseException(e).getErrorMessage());
      setSuccess(false);
    } finally {
      setIsFetching(false);
    }
  };

  const deleteList = async id => {
    try {
      await AxiosPrivate.delete(`/distribution-list/${id}`);
      return true;
    } catch (e) {
      setErrors(new ResponseException(e).getErrorMessage());
    }
  };

  return {
    success,
    errors,
    isFetching,
    displayErrors,
    items,
    count,
    getAll,
    getDetails,
    submit,
    duplicate,
    deleteList,
  };
};
